import $ from "jquery";
import _ from "lodash";
import config from "config";
import Bluebird from "bluebird";
import requestTracker from "services/activeRequestTracker";
import LOG from "services/loggingService";
import HttpError from "util/HttpError";
import { sessionService } from "services/sessionService";
import { genesysApp } from "util/genesysAppHeader";
import authClientService from "services/authClientService";

const apiRoot = config.api.uri;
let apiVersion = null;
const REQUEST_TIMEOUT = config.api.timeout;

const queryStr = function (parameters) {
    let query = "";
    const arrayParameterCollection = {};
    let params;

    if (parameters && !_.isEmpty(parameters)) {
        // Get all parameters that are simple key-value pairs and where the value is
        // NOT an array
        params = _.reduce(
            parameters,
            function (result, value, key) {
                if (_.isArray(parameters[key])) {
                    arrayParameterCollection[key] = value;
                } else {
                    result[key] = value;
                }

                return result;
            },
            {}
        );

        // serialize the parameters
        query = "?" + $.param(params);

        _.forIn(arrayParameterCollection, function (arr, key) {
            // convert the array values into query parameters that will result in:
            //   ?key=value1&key=value2&...
            _.each(arr, function (item) {
                if (query === "?") {
                    query += key + "=" + item;
                } else {
                    query += "&" + key + "=" + item;
                }
            });
        });
    }
    return query;
};

const buildPath = function (path, parameters) {
    let url = apiRoot + path;
    if (path.indexOf("http") === 0) {
        // If the path argument is a full URL, then don't prefix it with the api root
        url = path;
    }

    if (apiVersion) {
        url = url.replace(/\/api\/v[0-9]*\//, "/api/" + apiVersion + "/");
    }
    return url + queryStr(parameters);
};

/**
 * Call the api.
 * @returns {promise} a jquery promise object that will resolve with the response from
 *                    the server. If a problem occurs with the request, catch()
 *                    handlers that are registered with the promise can expect the
 *                    following arguments to their handler:
 *
 *                        .catch(function({object} error) { });
 *
 *                    - {object} error An object that contains data about the speicifc
 *                                     error that occurred in the request. This object
 *                                     will contain the following attributes:
 *
 *                                       - type {string}
 *                                           The type of error that occurred. Possible
 *                                           values may inclue "timeout", "abort",
 *                                           "parsererror", HTTP status codes.
 *                                       - details {object} (optional)
 *                                           For certain types of errors, an object
 *                                           may be included in the error that will
 *                                           contain more information about the error
 *                                           that occurred.
 */
const call = function (method, path, data, parameters, customHeaders) {
    const url = buildPath(path, parameters);

    const options = {
        type: method,
        url: url,
        contentType: "application/json; charset=UTF-8",
        dataType: "text",
        processData: false,
        timeout: REQUEST_TIMEOUT,
    };

    /** Temporary workaround for IE 11 caching issues. TODO: Remove on completion of UI-1582 **/
    if (navigator.userAgent.indexOf("Trident") > 0) {
        options.cache = false;
    }

    if (data) {
        options.data = JSON.stringify(data);
    }

    options.headers = {
        "ININ-Client-Path": window.location.hash,
    };

    options.headers["Genesys-App"] = genesysApp;

    // Add custom headers if provided
    if (customHeaders) {
        Object.assign(options.headers, customHeaders);
    }

    // Get fresh token for every API call as recommended
    return authClientService
        .getToken()
        .catch((getTokenError) => {
            // getToken failed - graceful fallback
            LOG.warn("Failed to get auth token:", getTokenError);
            sessionService.isSessionValid(false);
            return Bluebird.reject(new HttpError(getTokenError));
        })
        .then((currentToken) => {
            if (currentToken) {
                options.headers.Authorization = "bearer " + currentToken;
                return makeAjaxCall(options, currentToken);
            } else {
                // No token available - don't notify host app, just mark session invalid
                sessionService.isSessionValid(false);
                return Bluebird.reject(
                    new HttpError({ responseText: "No auth token available" })
                );
            }
        });
};

function makeAjaxCall(options, currentToken) {
    const jqXHR = $.ajax(options);

    const promise = new Bluebird((resolve, reject, onCancel) => {
        onCancel(() => {
            LOG.trace("apiCore promise cancelled", options);
            jqXHR.abort();
        });
        return resolve(jqXHR);
    })
        .then((response) => {
            if (response) {
                return Bluebird.resolve(JSON.parse(response));
            } else {
                return Bluebird.resolve();
            }
        })
        .catch((error) => {
            if (error.status === 401) {
                sessionService.isSessionValid(false);
                // Notify the auth client about the 401 response
                if (currentToken) {
                    try {
                        authClientService.notify401Response(currentToken);
                    } catch (notifyError) {
                        LOG.warn("Failed to notify 401 response:", notifyError);
                    }
                }
                return Bluebird.reject(new HttpError(error));
            } else {
                return Bluebird.reject(new HttpError(error));
            }
        });

    requestTracker.addRequest(promise);

    return promise;
}

const restMethods = {
    /**
     * Gets a resource at the provided path.
     * @param {string} path the path to the object to get
     * @param {object} parameters a key-value object containing any query parameters
     *   to use in the request
     * @return {promise} a promise that resolves with the response from the server
     */
    get: function (path, parameters) {
        return call("GET", path, undefined, parameters);
    },

    /**
     * Patches a resource at the provided path.
     * @param {string} path the path to the object to get
     * @param {object} data the data that should be sent to the server
     * @param {object} parameters a key-value object containing any query parameters
     *   to use in the request
     * @return {promise} a promise that resolves with the response from the server
     */
    patch: function (path, data, parameters) {
        return call("PATCH", path, data, parameters);
    },

    /**
     * Puts a resource at the provided path.
     * @param {string} path the path to the object to get
     * @param {object} data the data that should be sent to the server
     * @param {object} parameters a key-value object containing any query parameters
     *   to use in the request
     * @return {promise} a promise that resolves with the response from the server
     */
    put: function (path, data, parameters) {
        return call("PUT", path, data, parameters);
    },

    /**
     * Posts a resource at the provided path.
     * @param {string} path the path to the object to get
     * @param {object} data the data that should be sent to the server
     * @param {object} parameters a key-value object containing any query parameters
     *   to use in the request
     * @param {object} customHeaders optional custom headers to include in the request
     * @return {promise} a promise that resolves with the response from the server
     */
    post: function (path, data, parameters, customHeaders) {
        return call("POST", path, data, parameters, customHeaders);
    },

    /**
     * Deletes a resource at the provided path.
     * @param {string} path the path to the object to get
     * @param {object} parameters a key-value object containing any query parameters
     *   to use in the request
     * @return {promise} a promise that resolves with the response from the server
     */
    del: function (path, parameters) {
        return call("DELETE", path, undefined, parameters);
    },

    /**
     * Generates a complete public-api url for the provided path
     * @param {string} path - the path to the object to get
     * @param {object} parameters - parameters a key-value object containing any query parameters
     *   to use in the request
     * @return {string} url - the public-api url
     */
    buildPath: buildPath,
};

/**
 * Api Core service. Handles basic ajax requests to our API as well
 * as parsing the error format returned by the API and providing
 * utilities for parsing those errors in specific service modules.
 * @exports services/apiCore
 */
const apiCore = _.assign({}, restMethods);

/**
 * Support multiple versions of the API. Version can be specified on a per request
 * basis by calling apiCore.<version>.<restMethod>(...), e.g. apiCore.v2.get(...).
 * Calling apiCore.<restMethod>(...) without a specified version will use the
 * version specified by the config.api.uri.
 */
const versions = ["v1", "v2"];
_.forEach(versions, function (version) {
    apiCore[version] = {};
    _.forEach(restMethods, function (method, key) {
        apiCore[version][key] = function () {
            apiVersion = version;
            const result = method.apply(this, arguments);
            apiVersion = null;
            return result;
        };
    });
});

export default apiCore;
